import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Users, Gavel, FileText, Timer, Volume2, LayoutGrid, RotateCcw } from 'lucide-react';
import RGL, { WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ElectionObserverView from '../components/observer/ElectionObserverView';
import { isMotionInOrder, MOTION_TYPES } from '../utils/motionTypes';
import QuorumStatus from '../components/common/QuorumStatus';
import JoinQrCard from '../components/meeting/JoinQrCard';
import { getQuorumStatus } from '../utils/quorumUtils';
import { getActiveConsentItems, isConsentCalendarAgendaRow } from '../utils/consentAgenda';
import {
  getMinuteEntryDisplayText,
  getMinuteEntryKindLabel,
  getMinuteEntryOutcomeLabel,
  getMinuteEntryOutcomePillClass,
  getMinuteEntryOriginalSubtext,
  buildActivityMinuteGroups,
  isCanonicalNestParentForActivity,
} from '../utils/motionMinuteFormatting';

const GridLayout = WidthProvider(RGL);

const DEFAULT_OBSERVER_LAYOUT = {
  editMode: false,
  gridCols: 12,
  layout: [
    { i: 'motion-progress', x: 0, y: 0, w: 12, h: 4, minW: 6, minH: 3 },
    { i: 'current-agenda', x: 0, y: 4, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'speaker-timer', x: 4, y: 4, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'speaker-queue', x: 8, y: 4, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'recent-activity', x: 0, y: 10, w: 12, h: 6, minW: 6, minH: 4 },
    { i: 'in-order-guide', x: 0, y: 16, w: 12, h: 11, minW: 6, minH: 6 },
  ],
  visibleCards: {
    'motion-progress': true,
    'current-agenda': true,
    'speaker-timer': true,
    'speaker-queue': true,
    'recent-activity': true,
    'in-order-guide': true,
  },
};

// Curated, lay-audience-friendly subset of MOTION_TYPES for the Observer
// "what's in order" guide — the full ~25-motion engine would be too dense
// for a screen meant to be glanced at during a live meeting.
const OBSERVER_GUIDE_MOTION_KEYS = [
  'MAIN',
  'AMEND',
  'PREVQ',
  'POINT_ORDER',
  'PARL_INQUIRY',
  'TABLE',
  'POSTPONE',
  'COMMIT',
  'RECONSIDER',
  'DIVISION_ASSEMBLY',
];

const ObserverView = ({
  theme = 'light',
  meetingSetup = {},
  currentMotion = null,
  motions = [],
  agendaEntries = [], // This is actually meetingMinutes from the main app
  currentAgendaIndex = 0,
  motionTypes = {},
  speakerQueue = [],
  speakerTimer = 0,
  isTimerRunning = false,
  meetingTimer = 0,
  motionStack = [],
  currentSpeaker = null,
  currentElection = null,
  candidateSpeechQueue = [],
  currentCandidate = null,
  inElectionMode = false,
  currentView = 'meeting',
  electionScreen = 1,
  electionNominees = [],
  electionBallotVotes = {},
  electionWriteInCandidates = [],
  electionInvalidBallots = 0,
  unanimousConsentRequests = [],
  pendingUCRequest = null,
  showMotionCharacteristics = true,
  onConsentSuggestRemoval = null,
  voteJoinCode = null,
  layoutConfig = DEFAULT_OBSERVER_LAYOUT,
  onLayoutChange = null,
  onToggleEditMode = null,
  onResetLayout = null,
  canEditLayout = false,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentMinutes, setRecentMinutes] = useState([]);
  const [inOrderGuideCollapsed, setInOrderGuideCollapsed] = useState(false);

  const inOrderGuide = useMemo(
    () =>
      OBSERVER_GUIDE_MOTION_KEYS.map((key) => MOTION_TYPES[key])
        .filter(Boolean)
        .map((motion) => ({
          key: motion.key,
          name: motion.name,
          description: motion.description,
          inOrder: isMotionInOrder(motion.key, currentMotion, motionStack),
        })),
    [currentMotion, motionStack]
  );
  const inOrderItems = inOrderGuide.filter((m) => m.inOrder);
  const notInOrderItems = inOrderGuide.filter((m) => !m.inOrder);

  const freshMotion = (m) => {
    if (!m || !motions.length) return m;
    const row = motions.find((x) => x.id === m.id);
    if (!row) return m;
    return {
      ...m,
      text: row.text,
      originalText: row.originalText ?? m.originalText,
      isAmended: row.isAmended ?? m.isAmended,
      amendments: row.amendments ?? m.amendments,
      status: row.status ?? m.status,
    };
  };

  const displayCurrentMotion = freshMotion(currentMotion);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Process meeting minutes for recent activity display (same logic as Chair View)
  useEffect(() => {
    const { groupedMinutes, disposedMainIds, topLevel } =
      buildActivityMinuteGroups(agendaEntries);

    const processedMinutes = topLevel.map((entry) => {
      const canNest = isCanonicalNestParentForActivity(entry, disposedMainIds);
      const nestedEntries =
        canNest && entry.motionData
          ? groupedMinutes[entry.motionData.id] || []
          : [];
      return {
        ...entry,
        hasNestedEntries: nestedEntries.length > 0,
        nestedEntries,
      };
    });

    setRecentMinutes(processedMinutes.slice(0, 10));
  }, [agendaEntries]);

  // If elections are happening (either active election or elections view), show the election observer view
  if (currentElection || currentView === 'elections') {
    return (
      <ElectionObserverView
        theme={theme}
        meetingSetup={meetingSetup}
        currentElection={currentElection}
        candidateSpeechQueue={candidateSpeechQueue}
        currentCandidate={currentCandidate}
        speakerTimer={speakerTimer}
        isTimerRunning={isTimerRunning}
        meetingTimer={meetingTimer}
        electionScreen={electionScreen}
        nominees={electionNominees}
        ballotVotes={electionBallotVotes}
        writeInCandidates={electionWriteInCandidates}
        invalidBallots={electionInvalidBallots}
        voteJoinCode={voteJoinCode}
        enableQrDeviceVoting={Boolean(meetingSetup.enableQrDeviceVoting)}
      />
    );
  }

  const getCurrentAgendaItem = () => {
    if (!meetingSetup.orderOfBusiness || currentAgendaIndex >= meetingSetup.orderOfBusiness.length) {
      return null;
    }
    return meetingSetup.orderOfBusiness[currentAgendaIndex];
  };

  const suggestConsentRemoval = (lineId, text) => {
    if (typeof window !== 'undefined' && window.opener && window.opener !== window) {
      try {
        window.opener.postMessage(
          { type: 'CONSENT_SUGGEST_PULL', lineId, text },
          window.location.origin
        );
      } catch {
        /* ignore */
      }
      return;
    }
    if (typeof onConsentSuggestRemoval === 'function') {
      onConsentSuggestRemoval(lineId, text);
    }
  };

  const consentAgendaCur = getCurrentAgendaItem();
  const showConsentOnObserver =
    consentAgendaCur &&
    isConsentCalendarAgendaRow(consentAgendaCur) &&
    !consentAgendaCur.completed;
  const observerActiveConsentItems = showConsentOnObserver
    ? getActiveConsentItems(meetingSetup.consentAgendaItems)
    : [];

  const getMotionStatus = () => {
    if (!currentMotion) return null;

    const status = {
      'pending_second': 'Awaiting Second',
      'seconded': 'Motion Seconded',
      'stated': 'Stated (legacy)',
      'open_debate': 'Stated / debate open',
      'close_debate': 'Debate Closed',
      'ready_for_vote': 'Ready for Vote',
      'voted_passed': 'Vote Complete - PASSED',
      'voted_failed': 'Vote Complete - FAILED',
      'completed': 'Motion Complete'
    };

    return status[currentMotion.status] || currentMotion.status;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderMotionCharacteristics = (motion) => {
    if (!showMotionCharacteristics || !motion || !motionTypes[motion.type]) {
      return null;
    }

    const characteristics = motionTypes[motion.type];
    const pills = [];

    // Add key characteristics as pills
    if (characteristics.needsSecond) {
      pills.push({ label: 'Needs Second', color: 'bg-blue-100 text-blue-800' });
    }
    if (characteristics.debatable) {
      pills.push({ label: 'Debatable', color: 'bg-green-100 text-green-800' });
    }
    if (characteristics.amendable) {
      pills.push({ label: 'Amendable', color: 'bg-purple-100 text-purple-800' });
    }
    if (characteristics.voteRequired) {
      const voteLabel = characteristics.voteRequired === 'majority' ? 'Majority Vote' :
                       characteristics.voteRequired === 'two_thirds' ? '2/3 Vote' :
                       characteristics.voteRequired === 'unanimous' ? 'Unanimous' :
                       characteristics.voteRequired.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      pills.push({ label: voteLabel, color: 'bg-orange-100 text-orange-800' });
    }

    return (
      <div className="flex flex-wrap gap-2">
        {pills.map((pill, index) => (
          <span
            key={index}
            className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold tracking-tight ${pill.color}`}
          >
            {pill.label}
          </span>
        ))}
      </div>
    );
  };

  const getMotionStages = (motion = currentMotion) => {
    // Stating the question opens debate in one step (no separate "open debate" stage).
    const stages = [
      { id: 'made', label: 'Made', status: 'pending_second' },
      { id: 'second', label: 'Second', status: 'seconded' },
      { id: 'stated_debate', label: 'Stated / debate', status: 'open_debate' },
      { id: 'put', label: 'Put', status: 'ready_for_vote' },
      { id: 'vote', label: 'Vote', status: 'voted_passed' },
      { id: 'announce', label: 'Announce', status: 'completed' }
    ];

    if (!motion) return stages;

    const getCurrentStageIndex = () => {
      switch (motion.status) {
        case 'pending_second': return 0;
        case 'seconded': return 1;
        case 'stated':
        case 'open_debate': return 2;
        case 'close_debate':
        case 'ready_for_vote': return 3;
        case 'voted_passed':
        case 'voted_failed': return 4;
        case 'completed': return 5;
        default: return 0;
      }
    };

    const currentIndex = getCurrentStageIndex();

    const processedStages = stages.map((stage, index) => {
      if (stage.id === 'stated_debate') {
        return {
          ...stage,
          isActive: motion.status === 'stated' || motion.status === 'open_debate',
          isCompleted: motion.status === 'close_debate' || motion.status === 'ready_for_vote' || motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded'
        };
      }

      if (stage.id === 'put') {
        return {
          ...stage,
          isActive: motion.status === 'close_debate',
          isCompleted: motion.status === 'ready_for_vote' || motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded' || motion.status === 'stated' || motion.status === 'open_debate'
        };
      }

      if (stage.id === 'vote') {
        return {
          ...stage,
          isActive: motion.status === 'ready_for_vote',
          isCompleted: motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded' || motion.status === 'stated' || motion.status === 'open_debate' || motion.status === 'close_debate'
        };
      }

      return {
        ...stage,
        isActive: index === currentIndex + 1,
        isCompleted: index <= currentIndex,
        isUpcoming: index > currentIndex + 1
      };
    });

    return processedStages;
  };

  const cardClass = theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const accentClass = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50';

  const queueLength = Array.isArray(speakerQueue) ? speakerQueue.length : 0;

  // Helper function to render minute entries (same logic as Chair View)
  const getMinuteEntryStatusStyling = (entry, hasPendingNestedEntries = false) => {
    // If this entry has pending nested entries (like amendments), gray it out
    if (hasPendingNestedEntries) {
      return 'bg-gray-50 border-gray-300 opacity-60';
    }

    if (entry.type === 'consent_batch_summary' || entry.type === 'consent_item_adopted') {
      return theme === 'dark'
        ? 'bg-emerald-950/50 border-emerald-500'
        : 'bg-emerald-50 border-emerald-500';
    }
    if (entry.type === 'consent_batch_rejected') {
      return theme === 'dark'
        ? 'bg-orange-950/50 border-orange-400'
        : 'bg-orange-50 border-orange-400';
    }

    // Primary: Use result status if available (highest priority)
    const result = entry.result || entry.motionData?.result;
    if (result) {
      if (result === 'passed' || result === 'passed_by_uc') {
        return 'bg-green-50 border-green-400';
      } else if (result === 'failed') {
        return 'bg-red-50 border-red-400';
      } else if (result === 'referred_to_committee') {
        return 'bg-blue-50 border-blue-400';
      }
    }

    // Secondary: Pending status (high priority)
    if (entry.status === 'pending') {
      return 'bg-yellow-50 border-yellow-400';
    }

    // Tertiary: Color coding by entry type (before general status)
    if (entry.type === 'uc_request' || entry.type === 'unanimous_consent') {
      return 'bg-purple-50 border-purple-400'; // UC requests → purple
    } else if (entry.type === 'agenda_completion') {
      return 'bg-blue-50 border-blue-400'; // Agenda items → blue
    } else if (entry.type === 'call_to_order') {
      return 'bg-purple-50 border-purple-400';
    } else if (entry.type === 'election') {
      return 'bg-orange-50 border-orange-400';
    } else if (entry.type === 'chair_note') {
      return 'bg-sky-50 border-sky-400';
    }

    // Quaternary: Completed status for motions
    if (entry.status === 'completed') {
      return entry.type === 'motion_stated' || entry.type === 'motion_completed' ? 'bg-green-50 border-green-400' :
        'bg-gray-50 border-gray-300';
    }

    // Fallback: Use entry type
    return entry.type === 'motion_made' ? 'bg-blue-50 border-blue-400' :
      entry.type === 'motion_seconded' ? 'bg-green-50 border-green-300' :
      entry.type === 'chair_note' ? 'bg-sky-50 border-sky-400' :
      entry.type === 'call_to_order' ? 'bg-purple-50 border-purple-400' :
      entry.type === 'adjournment' ? 'bg-gray-50 border-gray-400' :
      entry.type === 'election' ? 'bg-orange-50 border-orange-400' :
      'bg-gray-50 border-gray-300';
  };

  const getEntryTypeTag = (entry) => {
    const baseClasses = "px-2 py-0.5 rounded text-xs font-normal whitespace-nowrap";
    const colorClasses = entry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
      entry.type === 'consent_batch_summary' || entry.type === 'consent_item_adopted' ? 'bg-emerald-100 text-emerald-900' :
      entry.type === 'consent_batch_rejected' ? 'bg-orange-100 text-orange-900' :
      entry.type === 'consent_agenda_pull' ? 'bg-amber-100 text-amber-900' :
      entry.type === 'amendment_result' ? 'bg-purple-100 text-purple-700' :
      entry.type === 'motion_stated' || entry.type === 'motion_completed' ? 'bg-gray-100 text-gray-700' :
      entry.type === 'motion_made' ? 'bg-gray-100 text-gray-700' :
      entry.type === 'motion_seconded' ? 'bg-gray-100 text-gray-700' :
      entry.type === 'chair_note' ? 'bg-sky-100 text-sky-900' :
      entry.type === 'call_to_order' ? 'bg-purple-100 text-purple-700' :
      entry.type === 'adjournment' ? 'bg-gray-100 text-gray-700' :
      entry.type === 'election' ? 'bg-orange-100 text-orange-700' :
      entry.type === 'unanimous_consent' || entry.type === 'uc_request' ? 'bg-purple-100 text-purple-700' :
      entry.type === 'agenda_completion' ? 'bg-blue-100 text-blue-700' :
      'bg-gray-100 text-gray-700';

    return `${baseClasses} ${colorClasses}`;
  };

  return (
    <div className={`min-h-screen p-2 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`} data-tour="observer-root">
      {/* Header */}
      <div className={`${cardClass} rounded-lg shadow-lg p-3 mb-2`} data-tour="observer-header">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold mb-0.5">
              {meetingSetup.organizationName || 'Parliamentary Meeting'}
            </h1>
            <div className="flex items-center gap-3 text-sm" data-tour="observer-status">
              <span className="flex items-center gap-2">
                <Clock size={20} />
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="flex items-center gap-2">
                <Users size={20} />
                {meetingSetup.presentMembers || 0} / {meetingSetup.totalMembers || 0} Present
              </span>
              <span className="flex items-center gap-2">
                <Timer size={20} />
                Meeting:{' '}
                <span className="inline-block min-w-[5ch] font-mono tabular-nums">
                  {formatTime(meetingTimer)}
                </span>
              </span>
              {meetingSetup.totalMembers > 0 && (
                <QuorumStatus
                  quorumStatus={getQuorumStatus(meetingSetup)}
                  size="normal"
                  className="shrink-0"
                />
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-start gap-3">
            {meetingSetup.enableQrDeviceVoting ? (
              voteJoinCode ? (
                <div data-tour="observer-qr-voting">
                  <JoinQrCard joinCode={voteJoinCode} />
                </div>
              ) : (
                <div
                  data-tour="observer-qr-voting"
                  className="max-w-[11rem] rounded-xl border border-dashed border-slate-300 bg-white/90 p-3 text-center text-xs text-slate-500"
                >
                  Voting QR appears when the chair’s meeting room is ready.
                </div>
              )
            ) : (
              <div
                data-tour="observer-qr-voting"
                className="max-w-[14rem] rounded-xl border border-dashed border-slate-300 bg-white/90 p-3 text-center text-xs text-slate-500"
              >
                Enable QR device voting in the meeting wizard to show a join code here.
              </div>
            )}
            <div className="text-right">
              <div className="text-xl font-bold">OBSERVER VIEW</div>
              <div className="text-xs text-gray-500">Real-time Meeting Display</div>
            </div>
            {canEditLayout && onToggleEditMode && (
              <div className="flex shrink-0 flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={onToggleEditMode}
                  title={layoutConfig?.editMode ? 'Done editing layout' : 'Edit layout'}
                  className={`flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium transition-opacity ${
                    layoutConfig?.editMode
                      ? 'bg-blue-600 text-white opacity-100'
                      : 'bg-gray-200 text-gray-500 opacity-40 hover:opacity-90'
                  }`}
                >
                  <LayoutGrid size={12} />
                  {layoutConfig?.editMode ? 'Done' : 'Edit'}
                </button>
                {layoutConfig?.editMode && onResetLayout && (
                  <button
                    type="button"
                    onClick={onResetLayout}
                    title="Reset to default layout"
                    className="flex items-center gap-1 rounded-full bg-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-300"
                  >
                    <RotateCcw size={12} />
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {(() => {
      const motionProgressCard = (currentMotion || motionStack.find(m => m.type === 'MAIN')) ? (
        <div className={`${cardClass} rounded-lg shadow-lg p-3 mb-2`} data-tour="observer-motion">
          <div className="mb-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold flex items-center gap-2">
                <Gavel size={18} />
                Motion Progress
              </h2>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                currentMotion?.status === 'open_debate' ? 'bg-blue-100 text-blue-800' :
                currentMotion?.status === 'ready_for_vote' ? 'bg-yellow-100 text-yellow-800' :
                currentMotion?.status === 'voted_passed' ? 'bg-green-100 text-green-800' :
                currentMotion?.status === 'voted_failed' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {getMotionStatus()}
              </div>
            </div>
            {displayCurrentMotion && (
              <div className="rounded-lg p-3 mb-2 bg-slate-100 border border-slate-200">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xl sm:text-2xl font-extrabold tracking-tight">
                        {motionTypes[displayCurrentMotion.type]?.name || displayCurrentMotion.type}
                      </div>
                      {displayCurrentMotion.isAmended && displayCurrentMotion.type === 'MAIN' && (
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                          Amended
                        </span>
                      )}
                    </div>
                  </div>
                  {renderMotionCharacteristics(displayCurrentMotion)}
                </div>
                <div className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">
                  “{displayCurrentMotion.text}”
                </div>
                <div className="text-sm text-gray-600">
                  Made by {displayCurrentMotion.member} • {displayCurrentMotion.timestamp}
                  {displayCurrentMotion.secondedBy && ` • Seconded by ${displayCurrentMotion.secondedBy}`}
                </div>
              </div>
            )}
          </div>
          
          {/* Main Motion / on-hold Breadcrumbs. With no amendment pending, this shows the
              main motion's own live progress. With an amendment pending, it instead shows
              one dimmed "on hold" card per stack entry — the main motion plus any pending
              primary amendment it's nested under — while the live current motion's own
              stepper renders separately below (Secondary Motion Breadcrumb). */}
          {(() => {
            const isSecondaryMotionActive = displayCurrentMotion && displayCurrentMotion.type !== 'MAIN';

            if (!isSecondaryMotionActive) {
              const mainMotion = freshMotion(
                displayCurrentMotion && displayCurrentMotion.type === 'MAIN' ? displayCurrentMotion : null
              );
              if (!mainMotion) return null;
              return (
                <div className="mb-4 transition-opacity duration-300">
                  <h3 className="text-sm font-semibold mb-2 text-gray-700">
                    Main Motion
                    {mainMotion.isAmended ? ' · Amended' : ''}
                  </h3>
                  <div className="flex items-center space-x-2 overflow-x-auto py-2">
                    {getMotionStages(mainMotion).map((stage, index, stages) => (
                      <div key={stage.id} className="flex items-center">
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full text-base font-bold border-2 flex-shrink-0 ${
                          stage.isCompleted ? 'bg-green-500 border-green-500 text-white' :
                          stage.isActive ? 'bg-blue-500 border-blue-500 text-white' :
                          'bg-gray-300 border-gray-300 text-gray-600'
                        }`}>
                          {stage.isCompleted ? '✓' : index + 1}
                        </div>
                        <span className={`ml-2 text-base font-semibold whitespace-nowrap ${
                          stage.isCompleted ? 'text-green-600' :
                          stage.isActive ? 'text-blue-600' :
                          'text-gray-500'
                        }`}>
                          {stage.label}
                        </span>
                        {index < stages.length - 1 && (
                          <div className={`w-8 h-1 mx-3 ${stage.isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            const onHoldMotions = motionStack.map((m) => freshMotion(m));
            return onHoldMotions.map((stackMotion) => (
              <div
                key={stackMotion.id}
                className="mb-4 opacity-50 transition-opacity duration-300"
              >
                <h3 className="text-sm font-semibold mb-2 text-gray-500">
                  {stackMotion.type === 'MAIN' ? 'Main Motion' : motionTypes[stackMotion.type]?.name || stackMotion.type} (on hold)
                  {stackMotion.isAmended ? ' · Amended' : ''}
                </h3>
                <div className="flex items-center space-x-2 overflow-x-auto py-2">
                  {getMotionStages(stackMotion).map((stage, index, stages) => (
                    <div key={stage.id} className="flex items-center">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full text-base font-bold border-2 flex-shrink-0 bg-gray-300 border-gray-300 text-gray-500">
                        {stage.isCompleted ? '✓' : index + 1}
                      </div>
                      <span className="ml-2 text-base font-semibold whitespace-nowrap text-gray-400">
                        {stage.label}
                      </span>
                      {index < stages.length - 1 && (
                        <div className="w-8 h-1 mx-3 bg-gray-300" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
          
          {/* Secondary Motion Breadcrumb */}
          {(() => {
            const isSecondaryMotionActive = currentMotion && currentMotion.type !== 'MAIN';
            
            return isSecondaryMotionActive && currentMotion && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                <h3 className="text-sm font-semibold text-blue-700 mb-2">
                  {motionTypes[currentMotion.type]?.name || currentMotion.type} Progress
                </h3>
                <div className="flex items-center space-x-2 overflow-x-auto py-2">
                  {getMotionStages(currentMotion).map((stage, index) => (
                    <div key={stage.id} className="flex items-center">
                      <div className={`flex items-center justify-center w-12 h-12 rounded-full text-base font-bold border-2 flex-shrink-0 ${
                        stage.isCompleted ? 'bg-blue-500 border-blue-500 text-white' :
                        stage.isActive ? 'bg-orange-500 border-orange-500 text-white' :
                        'bg-gray-300 border-gray-300 text-gray-600'
                      }`}>
                        {stage.isCompleted ? '✓' : index + 1}
                      </div>
                      <span className={`ml-2 text-base font-semibold whitespace-nowrap ${
                        stage.isCompleted ? 'text-blue-600' :
                        stage.isActive ? 'text-orange-600' :
                        'text-gray-500'
                      }`}>
                        {stage.label}
                      </span>
                      {index < getMotionStages(currentMotion).length - 1 && (
                        <div className={`w-8 h-1 mx-3 ${
                          stage.isCompleted ? 'bg-blue-500' : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          
          {/* Vote Results Banner - moved into breadcrumbs card */}
          {currentMotion && (currentMotion.status === 'voted_passed' || currentMotion.status === 'voted_failed') && (
            <div className={`mt-4 p-3 rounded-lg text-center text-white text-xl font-bold ${
              currentMotion.status === 'voted_passed' ? 'bg-green-600' : 'bg-red-600'
            }`}>
              🗳️ VOTE RESULT: {currentMotion.status === 'voted_passed' ? 'MOTION PASSED' : 'MOTION FAILED'}
            </div>
          )}

          {/* Election Results Banner */}
          {currentElection && currentElection.status === 'completed' && (
            <div className={`mt-4 p-3 rounded-lg text-center text-white text-xl font-bold ${
              currentElection.winner ? 'bg-blue-600' : 'bg-gray-600'
            }`}>
              🏆 ELECTION RESULT: {currentElection.winner 
                ? `${currentElection.winner.toUpperCase()} ELECTED${currentElection.byAcclamation ? ' (BY ACCLAMATION)' : ''}`
                : 'NO CLEAR WINNER'
              }
            </div>
          )}
        </div>
      ) : null;

      const agendaCard = (
          <div
            className={`${cardClass} rounded-lg shadow-lg p-3 h-48 flex flex-col`}
            data-tour="observer-agenda"
          >
            <h2 className="text-base font-bold mb-2 flex items-center gap-2">
              <FileText size={18} />
              Agenda
            </h2>
            <div className="flex-1 flex flex-col justify-between overflow-hidden">
              {getCurrentAgendaItem() ? (
                <>
                  <div className="flex-shrink-0">
                    <div className="text-xs text-gray-500 mb-1">Current</div>
                    <div className="text-lg font-semibold mb-1 line-clamp-2">
                      {getCurrentAgendaItem().title}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      Item {currentAgendaIndex + 1} of {meetingSetup.orderOfBusiness?.length || 0}
                    </div>
                  </div>
                  {/* Next agenda item */}
                  <div className="flex-shrink-0">
                    {(() => {
                      const nextItemIndex = currentAgendaIndex + 1;
                      const nextItem = meetingSetup.orderOfBusiness?.[nextItemIndex];
                      return nextItem ? (
                        <div className={`p-2 rounded ${accentClass}`}>
                          <div className="text-xs text-gray-600 mb-1">Next</div>
                          <div className="text-xs font-medium line-clamp-2">{nextItem.title}</div>
                        </div>
                      ) : (
                        <div className={`p-2 rounded ${accentClass}`}>
                          <div className="text-xs text-gray-500">No more agenda items</div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-center">
                  No agenda item active
                </div>
              )}
            </div>
          </div>
      );

      const consentCalendarCard = showConsentOnObserver ? (
            <div
              className={`${cardClass} rounded-lg shadow-lg border-2 border-emerald-400/50 p-3`}
            >
              <h2 className="text-base font-bold mb-2 flex items-center gap-2 text-emerald-900">
                <FileText size={18} />
                Consent calendar
              </h2>
              <p className="text-xs text-gray-600 mb-2">
                Items proposed for adoption by general consent. If you believe an item should be considered separately,
                ask to remove it to the floor (chair decides).
              </p>
              {observerActiveConsentItems.length === 0 ? (
                <p className="text-sm text-gray-600">No items remain on the consent calendar.</p>
              ) : (
                <ul className="max-h-56 space-y-2 overflow-y-auto">
                  {observerActiveConsentItems.map((item) => (
                    <li
                      key={item.id}
                      className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-2 text-sm text-gray-900"
                    >
                      <div className="mb-2">{item.text || '(empty line)'}</div>
                      <button
                        type="button"
                        onClick={() => suggestConsentRemoval(item.id, item.text)}
                        className="w-full rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                      >
                        Suggest removal to the floor
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
      ) : null;

      const pendingUCCard = pendingUCRequest ? (
            <div className={`${cardClass} rounded-lg shadow-lg p-3 border-2 border-orange-300`}>
              <h2 className="text-base font-bold mb-2 flex items-center gap-2 text-orange-700">
                <Gavel size={18} />
                Unanimous Consent Request
              </h2>
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="text-base font-medium mb-2">
                  "{pendingUCRequest.text}"
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  Requested at {pendingUCRequest.timestamp || new Date().toLocaleTimeString()}
                </div>
                <div className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                  ⏳ Awaiting Response
                </div>
              </div>
            </div>
      ) : null;

      const speakerTimerCard = (
          <div className={`${cardClass} rounded-lg shadow-lg p-3 h-48 flex flex-col`} data-tour="observer-speaker-timer">
            <h2 className="text-sm font-bold mb-1 flex items-center gap-2">
              <Timer size={16} />
              Speaker Timer
            </h2>
            <div className="flex flex-1 flex-col justify-center text-center">
              <div className="mb-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                {currentSpeaker ? currentSpeaker : 'No speaker'}
              </div>
              <div
                className={`text-3xl sm:text-4xl font-mono font-bold mb-2 ${
                  (() => {
                    const timeLimit = 120;
                    const percentageLeft = speakerTimer / timeLimit;
                    if (percentageLeft > 0.5) return 'text-green-600';
                    if (percentageLeft > 0.25) return 'text-yellow-600';
                    return 'text-red-600';
                  })()
                }`}
              >
                {formatTime(speakerTimer)}
              </div>
              <div
                className={`inline-block mx-auto px-3 py-1.5 rounded-full text-xs font-medium ${
                  isTimerRunning ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {isTimerRunning && currentSpeaker ? 'Speaking' : isTimerRunning ? 'Speaking' : 'Timer idle'}
              </div>
            </div>
          </div>
      );

      const speakerQueueCard = (
          <div className={`${cardClass} rounded-lg shadow-lg p-3 h-48 flex flex-col`} data-tour="observer-speaker-queue">
            <h2 className="text-sm font-bold mb-1 flex items-center gap-2">
              <Volume2 size={16} />
              Speaker Queue
              {queueLength > 0 && (
                <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                  {queueLength}
                </span>
              )}
            </h2>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {queueLength > 0 ? (
                <div className="space-y-1.5">
                  {speakerQueue.slice(0, 5).map((speaker, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between rounded p-2 ${
                        index === 0
                          ? 'border-2 border-blue-300 bg-blue-100 text-blue-900'
                          : accentClass
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <div
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            index === 0 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <span className="truncate font-medium">
                          {typeof speaker === 'string' ? speaker : speaker?.name || 'Speaker'}
                        </span>
                      </div>
                      {index === 0 && !currentSpeaker && (
                        <span className="shrink-0 text-xs font-medium">Next</span>
                      )}
                    </div>
                  ))}
                  {speakerQueue.length > 5 && (
                    <div className={`rounded p-2 text-center text-xs text-gray-500 ${accentClass}`}>
                      +{speakerQueue.length - 5} more in queue
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  Queue empty
                </div>
              )}
            </div>
          </div>
      );

      const recentActivityCard = (
      <div className={`${cardClass} rounded-lg shadow-lg p-3`} data-tour="observer-activity">
        <h2 className="text-base font-bold mb-2 flex items-center gap-2">
          <FileText size={20} />
          Recent Activity
        </h2>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {recentMinutes.length === 0 ? (
            <p className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No business conducted yet.
            </p>
          ) : (
            recentMinutes.map((entry) => {
              // Check if entry has pending nested entries (like amendments)
              const hasPendingNestedEntries = entry.nestedEntries?.some(nested => nested.status === 'pending');

              return (
              <div key={entry.id} className={`p-3 rounded-lg border-l-4 ${getMinuteEntryStatusStyling(entry, hasPendingNestedEntries)}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={getEntryTypeTag(entry)}>
                      {getMinuteEntryKindLabel(entry, motionTypes)}
                    </span>
                    {entry.timestamp && (
                      <span className="text-xs text-gray-500">{entry.timestamp}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Status indicator */}
                    {entry.status === 'pending' && (
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium text-yellow-700">PENDING</span>
                      </div>
                    )}

                    {/* Amended tag */}
                    {(entry.isAmended || entry.motionData?.isAmended) && (
                      <span className="px-2 py-1 rounded text-xs font-bold bg-purple-100 text-purple-800">
                        AMENDED
                      </span>
                    )}

                    {/* Result tag */}
                    {(entry.result || entry.motionData?.result) && (
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getMinuteEntryOutcomePillClass(entry)}`}>
                        {getMinuteEntryOutcomeLabel(entry, motionTypes)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="whitespace-pre-line text-sm font-medium text-gray-900 mb-1">
                  {getMinuteEntryDisplayText(entry, motionTypes)}
                </div>
                {(() => {
                  const originalSubtext = getMinuteEntryOriginalSubtext(entry);
                  return originalSubtext ? (
                  <div className="text-xs text-gray-500 mb-1 italic">
                    {originalSubtext}
                  </div>
                  ) : null;
                })()}

                {/* Render nested subsidiary motions (like amendments) */}
                {entry.hasNestedEntries && entry.nestedEntries.length > 0 && (
                  <div className="ml-4 pl-3 border-l-2 border-gray-200 space-y-1 mt-2">
                    {entry.nestedEntries.map(nestedEntry => (
                      <div key={nestedEntry.id} className={`p-2 rounded-lg border-l-4 ${getMinuteEntryStatusStyling(nestedEntry)}`}>
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={getEntryTypeTag(nestedEntry)}>
                              {getMinuteEntryKindLabel(nestedEntry, motionTypes)}
                            </span>
                            {nestedEntry.timestamp && (
                              <span className="text-xs text-gray-500">{nestedEntry.timestamp}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {nestedEntry.status === 'pending' && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                <span className="text-xs font-medium text-yellow-700">PENDING</span>
                              </div>
                            )}
                            {(nestedEntry.result || nestedEntry.motionData?.result) && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getMinuteEntryOutcomePillClass(nestedEntry)}`}>
                                {getMinuteEntryOutcomeLabel(nestedEntry, motionTypes)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs font-medium text-gray-700 whitespace-pre-line">
                          {getMinuteEntryDisplayText(nestedEntry, motionTypes)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>
      );

      const inOrderGuideCard = (
        <div className={`${cardClass} rounded-lg shadow-lg p-3`} data-tour="observer-in-order-guide">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-base font-bold">
              <Gavel size={18} />
              What's in order right now
            </h2>
            <button
              type="button"
              onClick={() => setInOrderGuideCollapsed((v) => !v)}
              className="shrink-0 rounded-full border border-pp-line px-2.5 py-1 text-[11px] font-semibold text-gray-500 hover:bg-gray-50"
            >
              {inOrderGuideCollapsed ? 'Show' : 'Hide'}
            </button>
          </div>
          {!inOrderGuideCollapsed && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-pp-sage">
                  You may currently…
                </div>
                {inOrderItems.length === 0 ? (
                  <p className="text-sm text-gray-500">Nothing on this list is in order right now.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {inOrderItems.map((m) => (
                      <li
                        key={m.key}
                        className="rounded-lg border border-pp-sage/30 bg-pp-sageSoft/60 px-2.5 py-1.5 text-sm"
                      >
                        <span className="font-semibold">{m.name}</span>
                        <span className="text-gray-600"> — {m.description}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Not available right now
                </div>
                {notInOrderItems.length === 0 ? (
                  <p className="text-sm text-gray-500">Everything on this list is in order right now.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {notInOrderItems.map((m) => (
                      <li
                        key={m.key}
                        className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-500"
                      >
                        <span className="font-semibold">{m.name}</span>
                        <span> — {m.description}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      );

      const gridCards = {
        'motion-progress': motionProgressCard,
        'current-agenda': agendaCard,
        'speaker-timer': speakerTimerCard,
        'speaker-queue': speakerQueueCard,
        'recent-activity': recentActivityCard,
        'in-order-guide': inOrderGuideCard,
      };
      const editableLayout = (layoutConfig?.layout || []).filter(
        (item) => gridCards[item.i] && layoutConfig?.visibleCards?.[item.i] !== false
      );
      const editing = Boolean(layoutConfig?.editMode);

      return (
        <>
          {consentCalendarCard}
          {pendingUCCard}
          <GridLayout
            className="mb-2"
            cols={layoutConfig?.gridCols || 12}
            rowHeight={26}
            margin={[8, 8]}
            layout={editableLayout}
            onLayoutChange={(next) => onLayoutChange?.(next)}
            isDraggable={editing}
            isResizable={editing}
            draggableCancel="button,a,input,textarea,select"
          >
            {editableLayout.map((item) => (
              <div
                key={item.i}
                className={`rounded-lg ${
                  item.i === 'in-order-guide' || item.i === 'motion-progress' ? 'overflow-visible' : 'overflow-auto'
                } ${
                  editing ? 'ring-2 ring-blue-300/60' : ''
                }`}
              >
                {gridCards[item.i]}
              </div>
            ))}
          </GridLayout>
        </>
      );
      })()}
    </div>
  );
};

export default ObserverView;